import { useEffect, useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Copy, Mail, Phone, SearchCheck } from "lucide-react";
import { toast } from "sonner";

import { MigrationUserDetails } from "@/components/migration/migration-user-details";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import {
  formatAdminUserDisplayName,
  formatAdminUserSex,
  patientMetaField,
  primaryEmailDisplay,
  primaryPhoneDisplay,
} from "@/lib/admin-user-display";
import { initialsFromLabel } from "@/lib/messages/messages-helpers";
import { cn } from "@/lib/utils";

function copyValueToClipboard(value, successMessage) {
  const text = String(value || "").trim();
  if (!text) return;
  if (!navigator.clipboard?.writeText) {
    toast.error("Copy is not available in this environment.");
    return;
  }
  void navigator.clipboard.writeText(text).then(
    () => toast.success(successMessage),
    () => toast.error("Could not copy"),
  );
}

function HeaderChip({ children, tone = "muted" }) {
  const cls =
    tone === "primary"
      ? "border-primary/30 bg-primary/10 text-primary"
      : tone === "emerald"
        ? "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100"
        : "border-border/60 bg-muted/50 text-muted-foreground";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
        cls,
      )}
    >
      {children}
    </span>
  );
}

function QuickContactRow({ icon: Icon, label, value, copyAria, copySuccess }) {
  const text = String(value || "").trim();
  const empty = !text || text === "—";
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-md border border-border/50 bg-background/70 px-2.5 py-2 transition-colors hover:bg-background">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p
          className={cn(
            "min-w-0 truncate text-sm",
            empty ? "text-muted-foreground" : "text-foreground tabular-nums",
          )}
          title={empty ? undefined : text}
        >
          {empty ? "Not provided" : text}
        </p>
      </div>
      {!empty ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={copyAria}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => copyValueToClipboard(text, copySuccess)}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {copyAria}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * Read-only insurance card thumbnails. Cards are stored as `data:` image URIs, so they render
 * inline via <img>, but cannot be opened via <a target="_blank"> (browsers/Electron block top-level
 * navigation to data: URLs — that opened a blank window). Clicking opens an in-app lightbox instead,
 * mirroring the chat attachment image viewer.
 * @param {{ cards: string[] }} props
 */
function InsuranceCardThumbnails({ cards }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const activeSrc = activeIndex !== null ? cards[activeIndex] : null;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2">
        {cards.map((src, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActiveIndex(idx)}
            title="View insurance card photo"
            className="block h-20 w-28 overflow-hidden rounded-md border border-border/60 bg-muted transition hover:ring-2 hover:ring-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            <img
              src={src}
              alt={`Insurance card ${idx + 1}`}
              className="h-full w-full cursor-zoom-in object-cover"
            />
          </button>
        ))}
      </div>
      <Dialog
        open={activeSrc !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setActiveIndex(null);
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className={cn(
            "max-h-[min(92vh,92dvh)] w-auto max-w-[min(96vw,96dvw)] border-0 bg-transparent p-0 shadow-none",
            // Default close control is muted/low-opacity — invisible on bright images; force a solid one.
            "[&>button]:right-3 [&>button]:top-3 [&>button]:z-60 [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center",
            "[&>button]:rounded-full [&>button]:border-0 [&>button]:bg-black/70 [&>button]:text-white! [&>button]:opacity-100 [&>button]:shadow-md",
            "[&>button]:backdrop-blur-sm [&>button]:hover:bg-black/85 [&>button]:hover:text-white!",
          )}
        >
          <DialogTitle className="sr-only">Insurance card</DialogTitle>
          {activeSrc ? (
            <img
              src={activeSrc}
              alt="Insurance card"
              className="max-h-[min(88vh,88dvh)] max-w-[min(94vw,94dvw)] rounded-lg object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {object | null} props.user — admin user document from GET /api/admin/users/:id
 * @param {string} [props.avatarUrl]
 * @param {() => void | Promise<void>} [props.onRefreshPhoneDuplicates]
 * @param {boolean} [props.refreshingPhoneDuplicates]
 * @param {(nextValue: boolean) => void | Promise<void>} [props.onSaveCorporateInsurance] — persists metadata.corporate_insurance_status (shown for any user)
 * @param {boolean} [props.savingCorporateInsurance]
 */
export function ThreadMemberDetailsDialog({
  open,
  onOpenChange,
  user,
  avatarUrl,
  onRefreshPhoneDuplicates,
  refreshingPhoneDuplicates = false,
  onSaveCorporateInsurance,
  savingCorporateInsurance = false,
}) {
  const displayName = String(formatAdminUserDisplayName(user) || "").trim();
  const heroLabel = displayName || String(user?.id || "").trim() || "User";
  const initials = initialsFromLabel(heroLabel);
  const fallbackColor = getAvatarColorClasses(heroLabel);

  const pm = user?.patientMeta && typeof user.patientMeta === "object" ? user.patientMeta : {};
  const title = String(patientMetaField(pm, "title", "Title") || "").trim();
  const sex = formatAdminUserSex(user);
  const userType = String(user?.userType || "").trim();
  const status = String(user?.status || "").trim();

  const phone = primaryPhoneDisplay(user?.contacts);
  const email = primaryEmailDisplay(user?.contacts);

  const isActive = status.toLowerCase() === "active";

  // Corporate insurance status lives on the user's top-level `metadata` (bool | null),
  // owned by the concierge bot. Shown for any user in this dialog (not patient-only).
  const rawInsurance =
    user?.metadata && typeof user.metadata === "object"
      ? user.metadata.corporate_insurance_status
      : undefined;
  const currentInsurance = rawInsurance === true ? true : rawInsurance === false ? false : null;
  const [pendingInsurance, setPendingInsurance] = useState(currentInsurance);
  // Reset the pending choice when a different member is shown or the dialog reopens.
  useEffect(() => {
    setPendingInsurance(currentInsurance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, open]);
  const showInsuranceEditor = Boolean(user) && typeof onSaveCorporateInsurance === "function";

  // Patient-supplied insurance details (free-text + card photos), set by the patient in the mobile
  // app and stored on the user's `metadata`. Read-only here.
  const insuranceMetadata =
    user?.metadata && typeof user.metadata === "object" ? user.metadata : {};
  const insuranceDescription =
    typeof insuranceMetadata.insurance_description === "string"
      ? insuranceMetadata.insurance_description.trim()
      : "";
  const insuranceCards = Array.isArray(insuranceMetadata.insurance_cards)
    ? insuranceMetadata.insurance_cards.filter(
        (card) => typeof card === "string" && card.trim(),
      )
    : [];
  const showInsuranceDetails = Boolean(insuranceDescription) || insuranceCards.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92vh,860px)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <TooltipProvider delayDuration={300}>
          <DialogHeader className="shrink-0 space-y-0 border-b border-border/60 bg-muted/30 px-6 pb-4 pt-5 text-left">
            <DialogTitle className="sr-only">User details — {heroLabel}</DialogTitle>
            <DialogDescription className="sr-only">
              Full user profile loaded from the administrator API.
            </DialogDescription>
            <div className="flex items-start gap-3 pr-10">
              <Avatar className="h-14 w-14 shrink-0 ring-1 ring-border/60">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt={heroLabel} /> : null}
                <AvatarFallback className={cn("text-base font-semibold", fallbackColor)}>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-1.5">
                <p
                  className="truncate text-xl font-semibold leading-tight tracking-tight text-foreground"
                  title={heroLabel}
                >
                  {heroLabel}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  {title ? <HeaderChip>{title}</HeaderChip> : null}
                  {userType ? <HeaderChip tone="primary">{userType}</HeaderChip> : null}
                  {status ? (
                    <HeaderChip tone={isActive ? "emerald" : "muted"}>{status}</HeaderChip>
                  ) : null}
                  {sex ? <HeaderChip>{sex}</HeaderChip> : null}
                </div>
              </div>
              {onRefreshPhoneDuplicates ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onRefreshPhoneDuplicates()}
                  disabled={!user || refreshingPhoneDuplicates}
                  className="shrink-0 self-start border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200"
                >
                  <SearchCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  {refreshingPhoneDuplicates ? "Checking…" : "Check duplicates"}
                </Button>
              ) : null}
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[min(72vh,640px)] min-h-0 px-6 pb-6 pt-4">
            <div className="space-y-5 pr-3">
              {/* Quick contact card — most-used info first */}
              <div className="grid gap-2 sm:grid-cols-2">
                <QuickContactRow
                  icon={Phone}
                  label="Phone"
                  value={phone}
                  copyAria="Copy phone"
                  copySuccess="Phone copied"
                />
                <QuickContactRow
                  icon={Mail}
                  label="Email"
                  value={email}
                  copyAria="Copy email"
                  copySuccess="Email copied"
                />
              </div>

              {showInsuranceEditor ? (
                <div className="rounded-md border border-border/50 bg-background/70 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Corporate insurance
                    </p>
                    {currentInsurance === null ? (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Not set
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="inline-flex overflow-hidden rounded-md border border-border/60">
                      <Button
                        type="button"
                        size="sm"
                        variant={pendingInsurance === true ? "default" : "ghost"}
                        className="h-8 rounded-none px-3"
                        disabled={savingCorporateInsurance}
                        aria-pressed={pendingInsurance === true}
                        onClick={() => setPendingInsurance(true)}
                      >
                        Yes
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={pendingInsurance === false ? "default" : "ghost"}
                        className="h-8 rounded-none border-l border-border/60 px-3"
                        disabled={savingCorporateInsurance}
                        aria-pressed={pendingInsurance === false}
                        onClick={() => setPendingInsurance(false)}
                      >
                        No
                      </Button>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-auto h-8"
                      disabled={
                        savingCorporateInsurance ||
                        pendingInsurance === null ||
                        pendingInsurance === currentInsurance
                      }
                      onClick={() => onSaveCorporateInsurance(pendingInsurance)}
                    >
                      {savingCorporateInsurance ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              ) : null}

              {showInsuranceDetails ? (
                <div className="rounded-md border border-border/50 bg-background/70 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Insurance details
                  </p>
                  {insuranceDescription ? (
                    <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground">
                      {insuranceDescription}
                    </p>
                  ) : null}
                  {insuranceCards.length > 0 ? (
                    <InsuranceCardThumbnails cards={insuranceCards} />
                  ) : null}
                </div>
              ) : null}

              {user ? <MigrationUserDetails user={user} compact={false} hideHeader /> : null}
            </div>
          </ScrollArea>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
